import React, { createContext, useEffect, useMemo, useState } from 'react';

import CURRENT_USER from '../../GraphQl/Queries/CURRENT_USER';
import { useLazyQuery, useMutation, useQuery } from 'react-apollo';
import PROJECTS_CONNECTION from '../../GraphQl/Queries/PROJECTS';
import CREATE_PROJECT from '../../GraphQl/Mutations/CREATE_PROJECT';
import GET_PROJECT from '../../GraphQl/Queries/GET_PROJECT';
import UPDATE_PROJECT from '../../GraphQl/Mutations/UPDATE_PROJECT';
import MILESTONES from '../../GraphQl/Queries/MILESTONES';
import CREATE_MILESTONE from '../../GraphQl/Mutations/CREATE_MILESTONE';
import UPDATE_MILESTONE from '../../GraphQl/Mutations/UPDATE_MILESTONE';
import DELETE_MILESTONE from '../../GraphQl/Mutations/DELETE_MILESTONE';
import GET_USERS_BASIC_DETAILS from '../../GraphQl/Queries/GET_USERS_BASIC_DETAILS';

const withProjectTasks = (project) =>
  project ? { ...project, tasks: project.assignedTasks || [] } : null;

const ProjectContext = createContext({});

export const ProjectProvider = (props) => {
  const { children } = props;

  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectBoardPagination, setProjectBoardPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [milestones, setMilestones] = useState([]);
  const [teamMemberUsers, setTeamMemberUsers] = useState([]);
  const [ownerUsers, setOwnerUsers] = useState([]);

  const { data } = useQuery(CURRENT_USER);
  const currentUser = data?.currentUser;

  const [fetchProjectsPaginated, { data: projectsConnectionData, loading: projectsLoading }] =
    useLazyQuery(PROJECTS_CONNECTION, {
      fetchPolicy: 'no-cache',
    });

  const [
    fetchProject,
    { data: projectData, loading: projectLoading, error: projectError, refetch: refetchProject },
  ] = useLazyQuery(GET_PROJECT, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  const [createProject, { loading: createProjectLoading }] = useMutation(CREATE_PROJECT);
  const [updateProject, { loading: updateProjectLoading }] = useMutation(UPDATE_PROJECT);

  const [fetchMilestones, { data: milestonesData, loading: milestonesLoading }] = useLazyQuery(
    MILESTONES,
    {
      fetchPolicy: 'no-cache',
    }
  );
  const [createMilestone, { loading: createMilestoneLoading }] = useMutation(CREATE_MILESTONE);
  const [updateMilestone, { loading: updateMilestoneLoading }] = useMutation(UPDATE_MILESTONE);
  const [deleteMilestone, { loading: deleteMilestoneLoading }] = useMutation(DELETE_MILESTONE);

  const [fetchTeamMemberUsers, { data: teamMemberUsersData, loading: teamMemberUsersLoading }] =
    useLazyQuery(GET_USERS_BASIC_DETAILS, {
      fetchPolicy: 'no-cache',
    });

  const [fetchOwnerUsers, { data: ownerUsersData, loading: ownerUsersLoading }] =
    useLazyQuery(GET_USERS_BASIC_DETAILS, {
      fetchPolicy: 'no-cache',
    });

  const currentProject = useMemo(
    () => withProjectTasks(projectData && projectData.project),
    [projectData]
  );

  useEffect(() => {
    if (projectsConnectionData && projectsConnectionData.projectsConnection) {
      setProjects(
        projectsConnectionData.projectsConnection.edges.map((edge) => withProjectTasks(edge.node))
      );
      setProjectBoardPagination((prev) => ({
        ...prev,
        total: projectsConnectionData.full.aggregate.count,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectsConnectionData]);

  useEffect(() => {
    if (milestonesData && milestonesData.milestonesConnection) {
      setMilestones(milestonesData.milestonesConnection.edges.map((edge) => edge.node));
    }
  }, [milestonesData]);

  useEffect(() => {
    if (teamMemberUsersData && teamMemberUsersData.users) {
      setTeamMemberUsers(teamMemberUsersData.users);
    }
  }, [teamMemberUsersData]);

  useEffect(() => {
    if (ownerUsersData && ownerUsersData.users) {
      setOwnerUsers(ownerUsersData.users);
    }
  }, [ownerUsersData]);

  return (
    <ProjectContext.Provider
      value={{
        currentUser,
        fetchProjectsPaginated,
        projectsConnectionData,
        projectsLoading,
        projects,
        setProjects,
        projectBoardPagination,
        setProjectBoardPagination,
        selectedProject,
        setSelectedProject,
        createProject,
        createProjectLoading,
        fetchProject,
        currentProject,
        projectLoading,
        projectError,
        refetchProject,
        updateProject,
        updateProjectLoading,
        fetchMilestones,
        milestones,
        milestonesLoading,
        createMilestone,
        createMilestoneLoading,
        updateMilestone,
        updateMilestoneLoading,
        deleteMilestone,
        deleteMilestoneLoading,
        fetchTeamMemberUsers,
        teamMemberUsers,
        teamMemberUsersLoading,
        fetchOwnerUsers,
        ownerUsers,
        ownerUsersLoading,
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
};

export default ProjectContext;